import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Globe, Trash2, Plus, Upload, ExternalLink, Search, X } from "lucide-react";
import { getImageUrl } from "../../utils/imageUtils";
import { useClickOutside } from "../../hooks/useClickOutside";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
 * The marketing site is server-rendered by the API, not by this React app, and
 * the two live on different origins everywhere (localhost:3000 vs :8100,
 * v2.edisplayroom.com vs v2-api.edisplayroom.com). A bare "/it-website" href
 * therefore pointed at the admin's own origin and never reached the site.
 */
const PREVIEW_URL = `${process.env.REACT_APP_BACKEND_URL}/it-website`;

/**
 * CMS for the Infinity Touch marketing site (infinitytouch.app).
 *
 * The public page is server-rendered from Express (api/src/modules/website), so
 * this edits content only — the layout is fixed and cannot be broken from here.
 * Each section saves independently via PUT /api/website/admin/content/:section,
 * so a mistake in one section can't take the whole page down with it.
 *
 * The product grid is a *curated selection* of existing catalog products. The
 * picker searches the infinity-channel catalog and stores product ids; nothing
 * here ever creates a product.
 */

/* Field descriptors per section, so the forms stay declarative. */
const TEXT = (key, label, opts = {}) => ({ key, label, type: "text", ...opts });
const AREA = (key, label, opts = {}) => ({ key, label, type: "textarea", ...opts });
const IMAGE = (key, label) => ({ key, label, type: "image" });
const HTML = (key, label) => ({ key, label, type: "textarea", html: true });

const SECTIONS = [
  {
    key: "site", title: "Site & branding",
    blurb: "Logo, contact details and app-store links. Used in the header, footer and hero.",
    fields: [
      IMAGE("logo_url", "Logo"), IMAGE("favicon_url", "Favicon"),
      TEXT("address", "Address"), TEXT("phone", "Phone"), TEXT("email", "Email"),
      TEXT("appstore_url", "App Store URL"), TEXT("playstore_url", "Google Play URL"),
    ],
    lists: [{ key: "socials", label: "Social links", fields: [TEXT("platform", "Platform (css class)"), TEXT("icon_class", "Icon class"), TEXT("href", "URL")] }],
  },
  {
    key: "seo", title: "SEO",
    blurb: "Page title, meta description and indexing.",
    fields: [TEXT("title", "Title"), AREA("description", "Meta description"), IMAGE("og_image_url", "Share image"), TEXT("robots", "Robots")],
  },
  {
    key: "topbar", title: "Top bar",
    fields: [TEXT("address", "Address"), TEXT("phone", "Phone")],
    lists: [{ key: "links", label: "Links", fields: [TEXT("label", "Label"), TEXT("href", "Href")] }],
  },
  {
    key: "nav", title: "Navigation", devOnly: true,
    blurb: "Top-level items. An item with children renders as a dropdown.",
    lists: [{ key: "items", label: "Menu items", fields: [TEXT("label", "Label"), TEXT("href", "Href"), TEXT("target", "Target")], nested: { key: "children", label: "Dropdown items", fields: [TEXT("label", "Label"), TEXT("href", "Href"), TEXT("target", "Target")] } }],
  },
  {
    key: "hero", title: "Hero carousel",
    fields: [TEXT("interval_ms", "Slide interval (ms)")],
    lists: [{ key: "slides", label: "Slides", fields: [TEXT("eyebrow", "Eyebrow"), TEXT("title", "Title"), AREA("body", "Body"), IMAGE("image_url", "Image")] }],
  },
  {
    key: "category_cards", title: "Category cards", devOnly: true,
    fields: [IMAGE("banner_image_url", "Banner image"), AREA("callout_text", "Callout text"), TEXT("callout_cta_label", "CTA label"), TEXT("callout_cta_link", "CTA link")],
    lists: [{ key: "items", label: "Cards", fields: [IMAGE("image_url", "Image"), TEXT("title", "Title"), TEXT("link", "Link")] }],
  },
  {
    key: "how_it_works", title: "How it works", devOnly: true,
    fields: [TEXT("title", "Title"), AREA("subtitle", "Subtitle")],
    lists: [{ key: "steps", label: "Steps", fields: [IMAGE("icon_url", "Icon"), TEXT("step_label", "Step label"), TEXT("label_color", "Label colour class"), TEXT("title", "Title"), AREA("description", "Description")] }],
    nestedObject: { key: "app_block", label: "App & technology block", fields: [TEXT("title", "Title"), IMAGE("image_url", "Screenshot")] },
  },
  { key: "product_grid", title: "Product grid", custom: "products", blurb: "Curated from the Infinity Touch catalog. You pick which existing products appear — this never creates products." },
  {
    key: "offers", title: "Infinity Offers", devOnly: true,
    fields: [IMAGE("image_url", "Image"), TEXT("eyebrow", "Eyebrow"), HTML("headline_html", "Headline (HTML)"), AREA("body", "Body")],
  },
  {
    key: "about", title: "About",
    fields: [TEXT("title", "Title"), HTML("intro_html", "Intro (HTML)"), HTML("expanded_html", "Expanded (HTML)"), TEXT("read_more_label", "Read-more label"), TEXT("close_label", "Close label"), TEXT("cta_label", "CTA label"), TEXT("cta_link", "CTA link")],
  },
  { key: "download_app", title: "Download app banner", devOnly: true, fields: [TEXT("title", "Title")] },
  {
    key: "partner_advantage", title: "Partner advantage", devOnly: true,
    fields: [TEXT("title", "Title"), AREA("subtitle", "Subtitle"), HTML("right_column_html", "Right column (HTML)")],
    lists: [{ key: "features", label: "Features", fields: [IMAGE("icon_url", "Icon"), TEXT("title", "Title"), AREA("description", "Description")] }],
  },
  { key: "join_us", title: "Join as a Partner", devOnly: true, blurb: "Heading only — submissions go to Applications.", fields: [TEXT("title", "Title"), AREA("subtitle", "Subtitle")] },
  {
    key: "contact", title: "Contact",
    fields: [TEXT("title", "Title")],
    lists: [{ key: "info_boxes", label: "Info boxes", fields: [TEXT("icon_class", "Icon class"), TEXT("heading", "Heading"), AREA("text", "Text")] }],
  },
  {
    key: "testimonials", title: "Testimonials",
    fields: [TEXT("title", "Title")],
    lists: [{ key: "items", label: "Testimonials", fields: [TEXT("name", "Name"), AREA("quote", "Quote")] }],
  },
  {
    key: "footer", title: "Footer",
    fields: [HTML("info_html", "Address block (HTML)"), TEXT("copyright_text", "Copyright name"), TEXT("download_title", "Download heading")],
    lists: [{ key: "columns", label: "Link columns", fields: [TEXT("title", "Column heading")], nested: { key: "links", label: "Links", fields: [TEXT("label", "Label"), TEXT("href", "Href"), TEXT("target", "Target")] } }],
  },
];

/*
 * Sections flagged `devOnly` are structural rather than editorial — menus, step
 * lists, banner blocks. They stay in the descriptor list and the live page still
 * renders them from their saved content; they are simply kept out of the admin
 * until the planned developer login can gate them properly.
 */
const VISIBLE_SECTIONS = SECTIONS.filter((s) => !s.devOnly);

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });

export default function AdminWebsiteContent() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSection, setSavingSection] = useState(null);
  const [activeKey, setActiveKey] = useState(VISIBLE_SECTIONS[0].key);
  /* Sections edited since their last save — one section is on screen at a time,
   * so the rail has to say where the unsaved work is. */
  const [dirty, setDirty] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/website/admin/content`, authHeaders());
        setContent(r.data);
      } catch {
        toast.error("Failed to load website content");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Only registered while something is unsaved, so a clean page closes silently. */
  useEffect(() => {
    if (!dirty.size) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  /* Every edit in every section funnels through here, so it is the one place
   * that needs to know a section became dirty. */
  const setSection = (key, value) => {
    setContent((c) => ({ ...c, [key]: value }));
    setDirty((d) => (d.has(key) ? d : new Set(d).add(key)));
  };

  const save = async (key) => {
    setSavingSection(key);
    try {
      await axios.put(`${API}/website/admin/content/${key}`, content[key], authHeaders());
      setDirty((d) => {
        const next = new Set(d);
        next.delete(key);
        return next;
      });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSavingSection(null);
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Loading website content…</div>;
  if (!content) return <div className="p-6 text-red-600">Could not load website content.</div>;

  const active = VISIBLE_SECTIONS.find((s) => s.key === activeKey) || VISIBLE_SECTIONS[0];
  const saving = savingSection === active.key;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe className="h-6 w-6" /> Website Content
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Content for infinitytouch.app. The layout is fixed — you edit copy, images and lists.
          </p>
        </div>
        <a href={PREVIEW_URL} target="_blank" rel="noreferrer">
          <Button variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-2" />Preview site</Button>
        </a>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Section rail. Sticky against the dashboard's scrolling main column, so
            it stays put while a long form scrolls; a strip above the form below lg. */}
        <aside className="w-full lg:w-60 shrink-0 lg:sticky lg:top-0">
          <nav className="border rounded-lg bg-white p-1 flex lg:flex-col gap-1 overflow-x-auto">
            {VISIBLE_SECTIONS.map((section) => {
              const isActive = section.key === active.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setActiveKey(section.key)}
                  aria-current={isActive ? "true" : undefined}
                  className={`flex items-center justify-between gap-2 whitespace-nowrap lg:whitespace-normal rounded-md px-3 py-2 text-sm text-left transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <span className="truncate">{section.title}</span>
                  {dirty.has(section.key) && (
                    <span title="Unsaved changes" className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="flex-1 min-w-0 w-full border rounded-lg bg-white p-4 lg:p-6">
          <h2 className="text-lg font-semibold">{active.title}</h2>
          {active.blurb && <p className="text-xs text-slate-500 mt-1">{active.blurb}</p>}

          <div className="mt-4">
            {active.custom === "products" ? (
              <ProductGridEditor
                value={content.product_grid || { tabs: [] }}
                onChange={(v) => setSection("product_grid", v)}
              />
            ) : (
              <SectionForm
                section={active}
                value={content[active.key] || {}}
                onChange={(v) => setSection(active.key, v)}
              />
            )}
          </div>

          <div className="flex justify-end mt-6 pt-4 border-t">
            <Button onClick={() => save(active.key)} disabled={saving || !dirty.has(active.key)}>
              {saving ? "Saving…" : "Save section"}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ── generic section form ─────────────────────────────────────────────────── */

function SectionForm({ section, value, onChange }) {
  const patch = (p) => onChange({ ...value, ...p });

  return (
    <div className="space-y-6">
      {section.fields?.length > 0 && (
        <FieldGrid fields={section.fields} value={value} onChange={patch} />
      )}

      {section.nestedObject && (
        <div className="border rounded-md p-3 bg-slate-50">
          <p className="text-sm font-medium mb-3">{section.nestedObject.label}</p>
          <FieldGrid
            fields={section.nestedObject.fields}
            value={value[section.nestedObject.key] || {}}
            onChange={(p) => patch({ [section.nestedObject.key]: { ...(value[section.nestedObject.key] || {}), ...p } })}
          />
        </div>
      )}

      {section.lists?.map((list) => (
        <ListEditor
          key={list.key}
          list={list}
          items={value[list.key] || []}
          onChange={(items) => patch({ [list.key]: items })}
        />
      ))}
    </div>
  );
}

function FieldGrid({ fields, value, onChange }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fields.map((f) => (
        <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
          <Label className="text-xs">{f.label}</Label>
          <FieldInput field={f} value={value[f.key] ?? ""} onChange={(v) => onChange({ [f.key]: v })} />
        </div>
      ))}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const [uploading, setUploading] = useState(false);

  if (field.type === "image") {
    const upload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) return toast.error("Please choose an image");
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("hero", file);
        const r = await axios.post(`${API}/upload/hero`, fd, authHeaders());
        onChange(r.data.url);
      } catch {
        toast.error("Upload failed");
      } finally {
        setUploading(false);
      }
    };
    return (
      <div className="flex items-center gap-2 mt-1">
        {value ? (
          <img src={getImageUrl(value)} alt="" className="h-12 w-12 object-cover rounded border" />
        ) : (
          <div className="h-12 w-12 rounded border bg-slate-50" />
        )}
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="flex-1" placeholder="/it-website/img/…" />
        <label className="cursor-pointer">
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
          <span className="inline-flex items-center px-2 py-2 border rounded text-xs">
            <Upload className="h-4 w-4" />{uploading ? "…" : ""}
          </span>
        </label>
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <>
        <Textarea rows={field.html ? 4 : 2} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" />
        {field.html && <p className="text-[11px] text-slate-400 mt-1">HTML is rendered as-is on the page.</p>}
      </>
    );
  }

  return <Input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1" />;
}

/* ── repeatable lists ─────────────────────────────────────────────────────── */

function ListEditor({ list, items, onChange }) {
  const blank = () => {
    const o = { order: items.length, is_active: true };
    list.fields.forEach((f) => { o[f.key] = ""; });
    if (list.nested) o[list.nested.key] = [];
    return o;
  };
  const update = (i, patch) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const move = (i, delta) => {
    const next = [...items];
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next.map((it, idx) => ({ ...it, order: idx })));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium">{list.label} <span className="text-slate-400">({items.length})</span></p>
        <Button variant="outline" size="sm" onClick={() => onChange([...items, blank()])}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={i} className="border rounded-md p-3 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-slate-400">#{i + 1}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}>↑</Button>
                <Button variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓</Button>
                <label className="text-xs flex items-center gap-1 mr-2">
                  <input type="checkbox" checked={item.is_active !== false} onChange={(e) => update(i, { is_active: e.target.checked })} />
                  Visible
                </label>
                <Button variant="ghost" size="sm" onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
            <FieldGrid fields={list.fields} value={item} onChange={(p) => update(i, p)} />
            {list.nested && (
              <div className="mt-3 pl-3 border-l-2">
                <ListEditor
                  list={list.nested}
                  items={item[list.nested.key] || []}
                  onChange={(sub) => update(i, { [list.nested.key]: sub })}
                />
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-xs text-slate-400">Nothing yet.</p>}
      </div>
    </div>
  );
}

/* ── product grid ─────────────────────────────────────────────────────────── */

/* Ids are resolved in batches so a large grid can't overrun the query string. */
const ID_CHUNK = 200;

/**
 * Curated grid editor.
 *
 * Tabs and colour swatches are editorial: the Infinity Touch catalog has no
 * colour dimension (no variants, no customizations), so the swatch row cannot be
 * derived — you define it and choose which catalog products sit under each.
 * A tab's collection only filters the picker; the saved value is always the
 * explicit list of product ids.
 */
function ProductGridEditor({ value, onChange }) {
  const [collections, setCollections] = useState([]);
  /* product_id -> { product_id, name, image_url } for every id on the page. */
  const [catalog, setCatalog] = useState({});
  /* Ids we have already looked up, hit or miss — a miss is a real answer. */
  const [resolved, setResolved] = useState(() => new Set());
  const tabs = value.tabs || [];

  useEffect(() => {
    axios.get(`${API}/website/admin/catalog/collections`, authHeaders())
      .then((r) => setCollections(r.data || []))
      .catch(() => setCollections([]));
  }, []);

  const selectedIds = useMemo(
    () => [...new Set(tabs.flatMap((t) => (t.swatches || []).flatMap((s) => s.product_ids || [])))],
    [tabs],
  );

  /*
   * Resolve every selected id once, for the whole grid.
   *
   * Each picker used to fetch an unfiltered first-100 window of the catalog and
   * fall back to displaying the raw id for anything outside it — with ~500
   * infinity products that mislabelled most of the selection, and issued one
   * request per swatch to do it.
   */
  useEffect(() => {
    const pending = selectedIds.filter((id) => !resolved.has(id));
    if (!pending.length) return undefined;

    let cancelled = false;
    (async () => {
      const found = {};
      for (let i = 0; i < pending.length; i += ID_CHUNK) {
        const chunk = pending.slice(i, i + ID_CHUNK);
        try {
          const r = await axios.get(`${API}/website/admin/catalog/products`, {
            ...authHeaders(), params: { ids: chunk.join(",") },
          });
          (r.data || []).forEach((p) => { found[p.product_id] = p; });
        } catch {
          // Leave the ids unresolved rather than marking them unavailable — a
          // failed request says nothing about whether the product still exists.
          if (!cancelled) toast.error("Could not load product names");
          return;
        }
      }
      if (cancelled) return;
      setCatalog((prev) => ({ ...prev, ...found }));
      setResolved((prev) => new Set([...prev, ...pending]));
    })();

    return () => { cancelled = true; };
  }, [selectedIds, resolved]);

  /* Search results are catalog rows too, so a new pick is named immediately. */
  const learn = useCallback((products) => {
    if (!products || !products.length) return;
    setCatalog((prev) => {
      const next = { ...prev };
      products.forEach((p) => { next[p.product_id] = p; });
      return next;
    });
    setResolved((prev) => new Set([...prev, ...products.map((p) => p.product_id)]));
  }, []);

  const setTabs = (t) => onChange({ ...value, tabs: t });
  const updateTab = (i, patch) => setTabs(tabs.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Tabs <span className="text-slate-400">({tabs.length})</span></p>
        <Button variant="outline" size="sm"
          onClick={() => setTabs([...tabs, { label: "New tab", collection_id: "", order: tabs.length, is_active: true, swatches: [] }])}>
          <Plus className="h-4 w-4 mr-1" /> Add tab
        </Button>
      </div>

      {tabs.map((tab, i) => (
        <div key={i} className="border rounded-md p-3 bg-slate-50 space-y-3">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-xs">Tab label</Label>
              <Input value={tab.label || ""} onChange={(e) => updateTab(i, { label: e.target.value })} className="mt-1" />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Catalog collection (filters the picker only)</Label>
              <Select value={tab.collection_id || "none"} onValueChange={(v) => updateTab(i, { collection_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any collection</SelectItem>
                  {collections.map((c) => <SelectItem key={c.collection_id} value={c.collection_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="text-xs flex items-center gap-1 pb-2">
              <input type="checkbox" checked={tab.is_active !== false} onChange={(e) => updateTab(i, { is_active: e.target.checked })} />
              Visible
            </label>
            <Button variant="ghost" size="sm" className="pb-2" onClick={() => setTabs(tabs.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>

          <SwatchList
            swatches={tab.swatches || []}
            collectionId={tab.collection_id}
            catalog={catalog}
            resolved={resolved}
            onLearn={learn}
            onChange={(sw) => updateTab(i, { swatches: sw })}
          />
        </div>
      ))}
      {tabs.length === 0 && <p className="text-xs text-slate-400">No tabs — the product section will be empty.</p>}
    </div>
  );
}

function SwatchList({ swatches, collectionId, catalog, resolved, onLearn, onChange }) {
  const update = (i, patch) => onChange(swatches.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  return (
    <div className="pl-3 border-l-2 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">Colour swatches <span className="text-slate-400">({swatches.length})</span></p>
        <Button variant="outline" size="sm"
          onClick={() => onChange([...swatches, { label: "New", hex: "#cccccc", product_ids: [], order: swatches.length, is_active: true }])}>
          <Plus className="h-3 w-3 mr-1" /> Add swatch
        </Button>
      </div>
      {swatches.map((sw, i) => (
        <div key={i} className="border rounded p-2 bg-white">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Label</Label>
              <Input value={sw.label || ""} onChange={(e) => update(i, { label: e.target.value })} className="mt-1" />
            </div>
            <div className="w-28">
              <Label className="text-xs">Colour</Label>
              <Input type="color" value={sw.hex || "#cccccc"} onChange={(e) => update(i, { hex: e.target.value })} className="mt-1 h-9 p-1" />
            </div>
            <Button variant="ghost" size="sm" onClick={() => onChange(swatches.filter((_, idx) => idx !== i))}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
          <ProductPicker
            selected={sw.product_ids || []}
            collectionId={collectionId}
            catalog={catalog}
            resolved={resolved}
            onLearn={onLearn}
            onChange={(ids) => update(i, { product_ids: ids })}
          />
        </div>
      ))}
    </div>
  );
}

function ProductPicker({ selected, collectionId, catalog, resolved, onLearn, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  /*
   * Every swatch renders its own picker, so without this the result lists simply
   * accumulated: `open` was set on search and never cleared, and several lists
   * stayed on screen at once pushing the form around.
   */
  const boxRef = useClickOutside(() => setOpen(false), open);

  const search = async () => {
    try {
      const r = await axios.get(`${API}/website/admin/catalog/products`, {
        ...authHeaders(), params: { q: query, collection_id: collectionId || "", limit: 40 },
      });
      setResults(r.data || []);
      onLearn(r.data || []);
      setOpen(true);
    } catch {
      toast.error("Catalog search failed");
    }
  };

  const clear = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="mt-2" ref={boxRef}>
      <Label className="text-xs">Products <span className="text-slate-400">({selected.length} selected)</span></Label>
      <div className="flex flex-wrap gap-1 my-1">
        {selected.map((id) => {
          const product = catalog[id];
          /* Only a completed lookup can call an id unavailable. */
          const unavailable = !product && resolved.has(id);
          return (
            <span key={id}
              title={unavailable
                ? `${id} is no longer in the Infinity Touch catalog — the live page skips it`
                : id}
              className={`inline-flex items-center gap-1 text-xs border rounded px-2 py-1 ${
                unavailable ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-slate-100"
              }`}>
              {product ? product.name
                : unavailable ? `Unavailable — ${id}`
                : <span className="text-slate-400">Loading…</span>}
              <button type="button" onClick={() => onChange(selected.filter((x) => x !== id))}>
                <X className={`h-3 w-3 ${unavailable ? "text-amber-700" : "text-slate-500"}`} />
              </button>
            </span>
          );
        })}
        {!selected.length && <span className="text-xs text-slate-400">None selected.</span>}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); search(); }
              if (e.key === "Escape") setOpen(false);
            }}
            placeholder="Search the Infinity Touch catalog…" className="h-8 text-xs pr-7" />
          {query && (
            <button type="button" onClick={clear} aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={search}>
          <Search className="h-3 w-3" />
        </Button>
      </div>
      {open && (
        <div className="mt-2 max-h-48 overflow-auto border rounded bg-white">
          {results.map((p) => {
            const isSelected = selected.includes(p.product_id);
            return (
              <button key={p.product_id} type="button"
                className="w-full text-left px-2 py-1 text-xs hover:bg-slate-50 flex items-center justify-between"
                onClick={() => onChange(isSelected ? selected.filter((id) => id !== p.product_id) : [...selected, p.product_id])}>
                <span>{p.name}</span>
                <span className="text-slate-400">{isSelected ? "remove" : "add"}</span>
              </button>
            );
          })}
          {!results.length && <p className="text-xs text-slate-400 p-2">No matches.</p>}
        </div>
      )}
    </div>
  );
}
